import { CartAddEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Membership "+ Add" button (blocks/membership-price.liquid), on the product page and in Quick View.
 *
 * The block renders a plain <form action="/cart/add">, which on its own posts the whole page and
 * redirects to /cart. This adds it over AJAX exactly like the theme's product form does, then
 * dispatches a CartAddEvent so the theme's own listeners take over: the cart icon count updates,
 * the cart drawer re-renders and auto-opens.
 *
 * Because the page no longer reloads, it also keeps every "+ Add" button in sync with the cart:
 * hidden while the membership is in it, shown again once it is removed.
 */

const ACTIONS_SELECTOR = '.CP-ASplus-dialog-actions';

/**
 * @param {string | undefined} productId - The membership product id
 * @param {boolean} inCart - Whether the membership is now in the cart
 */
function setMembershipInCart(productId, inCart) {
  if (!productId) return;

  document.querySelectorAll(`${ACTIONS_SELECTOR}[data-membership-product-id="${productId}"]`).forEach((actions) => {
    if (actions instanceof HTMLElement) actions.hidden = inCart;
  });
}

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;

  const actions = form.closest(ACTIONS_SELECTOR);
  if (!(actions instanceof HTMLElement)) return;

  event.preventDefault();

  const button = form.querySelector('[type="submit"]');
  if (button instanceof HTMLButtonElement) {
    if (button.disabled) return;
    button.disabled = true;
  }

  const formData = new FormData(form);

  // Ask for the cart drawer's section HTML in the same request, as the theme's product form does
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  try {
    const fetchCfg = fetchConfig('javascript', { body: formData });
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: { ...fetchCfg.headers, Accept: 'text/html' },
    });
    const data = await response.json();

    if (data.status) {
      alert(data.description || data.message || 'Could not add the membership to your cart.');
      return;
    }

    setMembershipInCart(actions.dataset.membershipProductId, true);

    // Same payload as the theme's product form: `product-form-component` makes the cart icon add
    // this quantity to its count, and `sections` lets the cart drawer morph without a refetch
    form.dispatchEvent(
      new CartAddEvent({}, String(formData.get('id')), {
        source: 'product-form-component',
        itemCount: Number(formData.get('quantity')) || 1,
        sections: data.sections,
      })
    );
  } catch (error) {
    console.error('membership-add-to-cart:', error);
  } finally {
    if (button instanceof HTMLButtonElement) button.disabled = false;
  }
});

// Removing the membership in the cart drawer (or any other cart change) doesn't reload the page,
// so re-check the live cart and show or hide the buttons to match
document.addEventListener(ThemeEvents.cartUpdate, async () => {
  const productIds = new Set(
    Array.from(document.querySelectorAll(`${ACTIONS_SELECTOR}[data-membership-product-id]`)).map((actions) =>
      actions instanceof HTMLElement ? actions.dataset.membershipProductId : undefined
    )
  );
  productIds.delete(undefined);
  if (!productIds.size) return;

  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store' });
    const cart = await response.json();
    const cartProductIds = new Set((cart.items || []).map((/** @type {{ product_id: number }} */ item) => String(item.product_id)));

    productIds.forEach((productId) => setMembershipInCart(productId, cartProductIds.has(productId)));
  } catch (_) {
    // no-op: the buttons keep their current state
  }
});
